import React, { Component } from 'react';
import { cloneDeep } from 'lodash';

import FormContext from '../forms/contexts/Form';
import { CoreFormConsumer, CoreFormConsumerTypeEnum } from '../forms/';

import PickerContext from './context';
import { DateTimeRangePickerService } from './services/';
import { DatePickerProps, DateTimeRangePickerServiceShape } from './interfaces/';
import { DateTimeRangeContainer } from './containers/';
import { PICKER_PROPS } from './constants';
import { WaitForDelay, IsDefined } from '../../../utils/';

/**
 * CoreDateTimeRangePicker
 * @export
 * @class CoreDateTimeRangePicker
 * @extends {Component<DatePickerProps, {}>}
 */
export default class CoreDateTimeRangePicker extends Component<DatePickerProps, {}> {
  /**
   * Context
   * @static
   * @type {Readonly<typeof PickerContext>}
   * @memberof CoreDateTimeRangePicker
   */
  public static contextType: Readonly<typeof FormContext> = FormContext;

  /**
   * Default Props
   * @static
   * @type {Omit<DatePickerProps, 'name'>}
   * @memberof CoreDateTimeRangePicker
   */
  public static defaultProps: Omit<DatePickerProps, 'name'> = cloneDeep(PICKER_PROPS);

  /**
   * Abort Controller
   * @private
   * @type {AbortController}
   * @memberof CoreDateTimeRangePicker
   */
  private listener: AbortController;

  /**
   * Service
   * @private
   * @type {DateRangePickerServiceShape}
   * @memberof CoreDateTimeRangePicker
   */
  private service: DateTimeRangePickerServiceShape = new DateTimeRangePickerService();

  /**
   * LifeCycle Hook
   * @memberof CoreDateTimeRangePicker
   */
  /* istanbul ignore next */
  public componentDidMount() {
    const { props: { name } } = this;
    this.listener = new AbortController();
    try {
      if (!name) {
        throw new ReferenceError('CoreDateTimeRange requires prop[name].');
      }
    } catch (err: any) {
      /* istanbul ignore next */
      if (process.env.NODE_ENV !== 'production') {
        console.error(`DEVELOPER ERROR:: ${err.message || err}`);
      }
    }
  }

  /**
   * LifeCycle Hook
   * @memberof CoreDateTimeRangePicker
   */
  /* istanbul ignore next */
  public componentWillUnmount() {
    if (this.listener) {
      this.listener.abort();
    }
  }

  /**
   * Render
   * @returns
   * @memberof CoreDateTimeRangePicker
   */
  public render() {
    const { Recycle, service, props, context } = this;
    const { name } = props

    /* istanbul ignore next */
    if (!IsDefined(name)) {
      return null;
    }

    service.init(props, context);

    return (
      <PickerContext.Provider value={service}>
        <DateTimeRangeContainer />
        <CoreFormConsumer name={name} type={CoreFormConsumerTypeEnum.WATCHER}>
          {({ form, values: { value } }): void => {
            if (value) {
              service.init(props, context);
              /* istanbul ignore next */
              WaitForDelay().then(() => {
                service.broadcast();
              });
            }
            /* istanbul ignore next */
            if (form.dirty) {
              const matched: boolean = value === service.actual;
              if (!matched) {
                Recycle();
              }
            }
            return null;
          }}
        </CoreFormConsumer>
      </PickerContext.Provider>
    );
  }

  /**
   * Recycle
   * @protected
   * @memberof CoreDateTimeRangePicker
   */
  protected Recycle = (): void => {
    /* istanbul ignore next */
    WaitForDelay(0).then(() => {
      if (this.listener && !this.listener.signal.aborted) {
        this.service.reset();
      }
    });
  };
}
